
import React, { useState, useEffect } from 'react';
import { GoogleGenAI } from "@google/genai";
import { useJobDetail } from '../features/Jobs/hooks/useJobDetail';
import LoadingSpinner from '../components/LoadingSpinner';
import RoleBadge from '../features/Jobs/components/RoleBadge';
import { formatRelativeDate } from '../features/Jobs/utils/dateUtils';
import { Job, JobDetail, JobStatus, ALL_STATUSES } from '../features/Jobs/types';
import Breadcrumb from '../components/Breadcrumb';
import { getJobLocation } from '../features/Jobs/utils/jobUtils';
import ApplyJobModal from '../features/Jobs/components/ApplyJobModal';
import EditPatientDetailsModal from '../features/Jobs/components/EditPatientDetailsModal';
import { User } from '../App';
import { updateJob } from '../features/Jobs/services/jobsAPI';
import { navigateToJobs } from '../utils/navigation';

const parseSalary = (title: string | null | undefined): string | null => {
  if (!title) return null;
  const salaryRegex = /(?:RM|MYR)\s?[\d,]+(?:\.\d{2})?(?:\s?-\s?(?:RM|MYR)?\s?[\d,]+(?:\.\d{2})?)?/i;
  const match = title.match(salaryRegex);
  
  if (match && match[0]) {
    return match[0].toUpperCase().replace(/\s?-\s?/, ' - ');
  }
  
  return null;
};

const formatSalary = (salary: string | number | null | undefined): string | null => {
  if (salary === null || salary === undefined || String(salary).trim() === '') return null;
  const numericSalary = Number(salary);
  if (!Number.isNaN(numericSalary)) return `RM ${numericSalary.toLocaleString('en-US')}`;
  const salaryText = String(salary).trim();
  return /^(?:RM|MYR)\b/i.test(salaryText) ? salaryText : `RM ${salaryText}`;
};

const DetailSection: React.FC<{ title: string; action?: React.ReactNode; children: React.ReactNode }> = ({ title, action, children }) => (
  <section className="overflow-hidden rounded-[20px] border border-[#A1AAD9]/40 bg-white shadow-[0_12px_32px_rgba(90,70,170,0.06)]">
    <div className="flex min-h-[62px] items-center justify-between gap-4 border-b border-[#A1AAD9]/30 bg-gradient-to-r from-[#FFFAED] to-white px-5 py-3.5 sm:px-6">
      <h3 className="text-lg font-extrabold tracking-tight text-[#342F43]">{title}</h3>
      {action}
    </div>
    <div className="p-4 sm:p-5">{children}</div>
  </section>
);

const InfoRow: React.FC<{ label: string; value: React.ReactNode; className?: string }> = ({ label, value, className = "capitalize" }) => {
  if (value === null || value === undefined || value === '') return null;
  return (
    <div className="grid grid-cols-1 gap-1 border-b border-[#A1AAD9]/20 py-3 last:border-b-0 sm:grid-cols-[150px_minmax(0,1fr)] sm:gap-x-5">
      <dt className="text-xs font-bold uppercase tracking-[0.04em] text-[#636363]">{label}</dt>
      <dd className={`min-w-0 text-sm font-semibold leading-5 text-[#342F43] ${className}`}>{value}</dd>
    </div>
  );
};

const AIHealthSummary: React.FC<{
  condition?: string | null;
  history?: string | null;
  surgical?: string | null;
}> = ({ condition, history, surgical }) => {
  const [summary, setSummary] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const hasData = condition || history || surgical;

  useEffect(() => {
    if (!hasData) return;

    const fetchSummary = async () => {
      setLoading(true);
      try {
        const apiKey = import.meta.env.VITE_GEMINI_API_KEY || process.env.GEMINI_API_KEY;
        const ai = new GoogleGenAI({ apiKey: apiKey });
        const prompt = `
          You are a helpful medical assistant. Summarize the patient's health status into a concise paragraph (maximum 30 words) based on the following fields:
          
          Medical Condition: ${condition || 'None'}
          Medical History: ${history || 'None'}
          Surgical History: ${surgical || 'None'}

          IMPORTANT: Do not disclose any personal information such as the patient's name or mobile number if they appear in the text.
        `;

        const response = await ai.models.generateContent({
          model: 'gemini-2.5-flash',
          contents: prompt,
        });
        
        setSummary(response.text);
      } catch (error) {
        console.error("AI Summary Error:", error);
        const parts = [condition, history, surgical].filter(Boolean);
        setSummary(parts.join('. '));
      } finally {
        setLoading(false);
      }
    };

    fetchSummary();
  }, [condition, history, surgical, hasData]);

  if (!hasData) return null;

  return (
    <div className="grid grid-cols-1 gap-2 border-b border-[#A1AAD9]/20 py-3 sm:grid-cols-[150px_minmax(0,1fr)] sm:gap-x-5">
      <dt className="flex flex-wrap items-center gap-2 text-xs font-bold uppercase tracking-[0.04em] text-[#636363]">
        Health Condition
        <span className="inline-flex items-center rounded-full border border-[#A1AAD9]/45 bg-[#A1AAD9]/15 px-2 py-0.5 text-[9px] font-extrabold tracking-normal text-[#5A46AA]">
            AI Summary
        </span>
      </dt>
      <dd className="min-w-0 text-sm leading-5 text-[#342F43]">
        {loading ? (
            <div className="animate-pulse space-y-2">
                <div className="h-3.5 w-3/4 rounded bg-[#A1AAD9]/25"></div>
                <div className="h-3.5 w-1/2 rounded bg-[#A1AAD9]/20"></div>
            </div>
        ) : (
            <div className="prose prose-sm max-w-none font-medium text-[#342F43]">
                {summary}
            </div>
        )}
      </dd>
    </div>
  );
};

const PatientDetailsCard: React.FC<{ details: JobDetail }> = ({ details }) => {
  const groupedConditions = (details.conditions_list || []).reduce((acc, item) => {
    const category = item.primary_name || 'Others';
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(item.secondary_name);
    return acc;
  }, {} as Record<string, string[]>);

  const hasStructuredConditions = Object.keys(groupedConditions).length > 0;
  const medicalConditionText = details.health_condition?.trim();

  return (
    <div className="rounded-2xl border border-[#A1AAD9]/35 bg-[#FFFAED]/65 p-4 sm:p-5">
      <dl>
        <InfoRow label="Customer Name" value="Undisclosed" />
        <InfoRow label="Patient Age" value={details.age ?? "-"} />
        <InfoRow label="Patient Gender" value={details.sex} />
        <InfoRow label="Race" value={details.race} />
        <InfoRow label="Religion" value={details.religion} />
        <InfoRow label="Nationality" value={details.nationality} />
        <InfoRow label="Patient Height" value={details.height ? `${details.height} cm` : null} className="normal-case" />
        <InfoRow label="Patient Weight" value={details.weight ? `${details.weight} kg` : null} className="normal-case" />
        <InfoRow label="Location" value={details.location || details.address || ((details as any).add_city && details.add_state ? `${(details as any).add_city}, ${details.add_state}` : (details as any).add_city || details.add_state)} />
        
        {(hasStructuredConditions || medicalConditionText) && (
             <div className="grid grid-cols-1 gap-2 border-b border-[#A1AAD9]/20 py-3 sm:grid-cols-[150px_minmax(0,1fr)] sm:gap-x-5">
                <dt className="text-xs font-bold uppercase tracking-[0.04em] text-[#636363] sm:pt-1">Medical Conditions</dt>
                <dd className="min-w-0 text-sm text-[#342F43]">
                  {hasStructuredConditions ? Object.entries(groupedConditions).map(([category, items]) => (
                        <div key={category} className="mb-2.5 flex flex-wrap items-center gap-2 last:mb-0">
                            <span className="inline-flex min-h-7 items-center rounded-full border border-[#5A46AA] bg-[#5A46AA] px-2.5 text-xs font-bold text-white shadow-sm">
                                {category}
                            </span>
                            {(items as string[]).map((item, idx) => (
                                <span key={idx} className="inline-flex min-h-7 items-center rounded-full border border-[#A1AAD9]/55 bg-white px-2.5 text-xs font-semibold text-[#636363]">
                                    {item}
                                </span>
                            ))}
                        </div>
                    )) : <span className="font-medium leading-6">{medicalConditionText}</span>}
                </dd>
             </div>
        )}

        <AIHealthSummary condition={details.health_condition} history={details.medical_history} surgical={details.surgical_history} />

        {details.additional_info && (
          <div className="pt-4">
            <dt className="mb-2 text-xs font-bold uppercase tracking-[0.04em] text-[#636363]">Additional Info</dt>
            <dd
              className="prose prose-sm max-w-none rounded-xl border border-[#A1AAD9]/30 bg-white p-4 text-sm text-[#342F43]"
              dangerouslySetInnerHTML={{ __html: details.additional_info }}
            ></dd>
          </div>
        )}
      </dl>
    </div>
  );
};

const PatientDetailsWrapper: React.FC<{job: Job}> = ({job}) => {
  if (job.job_details && job.job_details.length > 0 && job.job_details[0].product) {
    return (
      <div className="space-y-2">
        {job.job_details.map((detail, index) => <PatientDetailsCard key={detail.id || index} details={{
          ...detail,
          age: job.age ?? detail.age,
          sex: job.job_gender || job.sex || job.cust_gender || detail.gender || detail.sex,
          health_condition: detail.health_condition || job.cust_medical_condition || job.health_condition || '',
          medical_history: detail.medical_history || job.cust_medical_history,
          surgical_history: detail.surgical_history || job.cust_surgical_history,
          conditions_list: detail.conditions_list?.length ? detail.conditions_list : job.cust_medical_condition_list,
        }} />)}
      </div>
    );
  }

  const syntheticDetail: JobDetail = {
    id: job.id,
    product: job.product_name,
    product_info: '',
    customer_name: job.customer_name || `${job.first_name || ''} ${job.last_name || ''}`.trim(),
    race: job.cust_race || job.race || '',
    age: job.age ?? job.job_details?.[0]?.age,
    sex: job.job_gender || job.sex || job.cust_gender || job.job_details?.[0]?.gender || job.job_details?.[0]?.sex || '',
    location: getJobLocation(job) || job.cust_location || job.location || '',
    health_condition: job.cust_medical_condition || job.health_condition || '',
    additional_info: job.additional_info || '',
    description: job.description || null,
    additional_health_info: job.cust_additional_health_info || job.additional_health_info,
    address: job.address,
    add_state: job.add_state,
    weight: job.cust_weight || job.weight,
    height: job.cust_height || job.height,
    medical_history: job.cust_medical_history,
    surgical_history: job.cust_surgical_history,
    conditions_list: job.cust_medical_condition_list,
    religion: job.cust_religion,
    nationality: job.cust_nationality
  };
  return <PatientDetailsCard details={syntheticDetail} />;
}

const OverviewItem: React.FC<{ icon: React.ReactNode; label: string; value: React.ReactNode | null }> = ({ icon, label, value }) => {
    if (value === null || value === undefined || value === '') return null;
    return (
        <div className="flex min-w-0 items-center gap-3 rounded-xl border border-[#A1AAD9]/30 bg-[#FFFAED] p-3 transition-colors hover:border-[#A1AAD9]/55 hover:bg-[#FFF5D7]/55" title={label}>
            <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-white text-[#5A46AA] shadow-sm ring-1 ring-[#A1AAD9]/25">
                {icon}
            </div>
            <div className="min-w-0 flex-grow">
                <div className="text-[10px] font-extrabold uppercase tracking-[0.08em] text-[#636363]">{label}</div>
                <div className="mt-1 break-words text-sm font-bold text-[#342F43]">{value}</div>
            </div>
        </div>
    );
};

const getStatusColor = (status: string) => {
  switch(status) {
    case JobStatus.open: return 'bg-green-100 text-green-800';
    case JobStatus.inProgress: return 'bg-blue-100 text-blue-800';
    case JobStatus.completed: return 'bg-teal-100 text-teal-800';
    case JobStatus.cancelled: return 'bg-red-100 text-red-800';
    case JobStatus.draft: return 'bg-gray-100 text-gray-800';
    case JobStatus.closed: return 'bg-gray-200 text-gray-800';
    default: return 'bg-gray-100 text-gray-800';
  }
}

const formatStatus = (s: string) => {
  if (!s) return '';
  const text = s.replace(/-/g, ' ');
  return text.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
}

const StatusEditor: React.FC<{ 
  status: string; 
  jobId: number; 
  isEditable: boolean 
}> = ({ status: initialStatus, jobId, isEditable }) => {
  const [status, setStatus] = useState(initialStatus);
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleStatusChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newStatus = e.target.value;
    setIsLoading(true);
    try {
      await updateJob(jobId, { status: newStatus });
      setStatus(newStatus);
      setIsEditing(false);
    } catch (error) {
      console.error("Failed to update status", error);
      alert("Failed to update status. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  if (isEditing) {
    return (
      <div className="flex items-center space-x-2">
        {isLoading ? (
           <span className="text-xs text-gray-500">Updating...</span>
        ) : (
          <select 
            value={status} 
            onChange={handleStatusChange}
            onBlur={() => setIsEditing(false)}
            autoFocus
            className="block min-h-10 w-full cursor-pointer rounded-xl border border-[#A1AAD9]/55 bg-white px-3 text-sm text-[#342F43] shadow-sm focus:border-[#5A46AA] focus:outline-none focus:ring-2 focus:ring-[#A1AAD9]/40"
          >
            {ALL_STATUSES.map((s) => (
              <option key={s} value={s}>{formatStatus(s)}</option>
            ))}
    </select>
        )}
      </div>
    );
  }

  return (
    <div 
      onClick={() => isEditable && setIsEditing(true)}
      className={`inline-flex items-center gap-2 rounded-lg transition-colors ${isEditable ? 'cursor-pointer group' : ''}`}
      role={isEditable ? "button" : undefined}
      title={isEditable ? "Click to change status" : undefined}
    >
      <span className={`inline-flex min-h-7 items-center rounded-full px-2.5 text-xs font-bold ${getStatusColor(status)}`}>
        {formatStatus(status)}
      </span>
      {isEditable && (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-[#A1AAD9] transition-colors group-hover:text-[#5A46AA]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
        </svg>
      )}
    </div>
  );
};

interface JobDetailPageProps {
  jobId: string;
  user: User;
}

const JobDetailPage: React.FC<JobDetailPageProps> = ({ jobId, user }) => {
  const { job, isLoading, error } = useJobDetail(jobId);
  const [isApplyModalOpen, setIsApplyModalOpen] = useState(false);
  const [isEditPatientModalOpen, setIsEditPatientModalOpen] = useState(false);
  
  const isCareManager = user.type === 'homecare' || user.type === 'superadmin';

  const handleBackClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    navigateToJobs();
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (error) {
    const breadcrumbItems = [{ label: 'Home', href: '/cg_jobs/' }];
    return (
      <>
        <Breadcrumb items={breadcrumbItems} />
        <div className="rounded-[20px] border border-red-200 bg-red-50 p-8 text-center text-red-800 shadow-sm sm:p-10">
          <span className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-red-100" aria-hidden="true"><svg className="h-6 w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 9v4m0 4h.01M10.3 3.8 2.6 17.1A2 2 0 0 0 4.3 20h15.4a2 2 0 0 0 1.7-2.9L13.7 3.8a2 2 0 0 0-3.4 0Z" /></svg></span>
          <h3 className="mb-2 text-xl font-extrabold">Could Not Load Job</h3>
          <p className="text-sm">Please try again or return to the jobs directory.</p>
          <a href="/cg_jobs/" onClick={handleBackClick} className="mt-5 inline-flex min-h-11 items-center rounded-xl bg-[#5A46AA] px-5 text-sm font-bold text-white transition hover:brightness-95 focus:outline-none focus:ring-2 focus:ring-[#5A46AA] focus:ring-offset-2">
            Back to Jobs
          </a>
        </div>
      </>
    );
  }

  if (!job) {
    return null;
  }

  const jobTitle = job.description || job.product_name || (job.job_details && job.job_details.length > 0 && job.job_details[0].product) || 'Job Details';
  const salary = formatSalary(job.salary) || parseSalary(job.product_name);
  const productService = job.job_details?.find(detail => detail.product)?.product || job.product_name;

  const breadcrumbItems = [
    { label: 'Home', href: '/cg_jobs/' },
    { label: jobTitle }
  ];

  return (
    <div className="animate-fade-in">
      <Breadcrumb items={breadcrumbItems} />

      <section className="relative mb-3 overflow-hidden rounded-[24px] border border-[#A1AAD9]/40 bg-gradient-to-br from-[#FFF5D7] via-[#FFFAED] to-white px-5 py-6 shadow-[0_14px_36px_rgba(90,70,170,0.08)] sm:px-7 sm:py-7" aria-labelledby="job-detail-title">
        <div className="relative z-[1] flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex min-w-0 flex-1 flex-col gap-3 sm:flex-row sm:items-start sm:gap-4">
            <span className="flex h-12 w-12 flex-none items-center justify-center rounded-2xl bg-white text-[#5A46AA] shadow-sm ring-1 ring-[#A1AAD9]/30" aria-hidden="true"><svg className="h-6 w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M20 7h-4V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2ZM8 7h8m-5 5h2" /></svg></span>
            <div className="min-w-0">
              <div className="mb-2 flex flex-wrap items-center gap-2">
                <RoleBadge role={job.role} />
                <span className="inline-flex min-h-7 items-center rounded-full border border-[#A1AAD9]/40 bg-[#FFF5D7]/90 px-3 text-xs font-bold text-[#5A46AA]">Job #{job.id}</span>
                <StatusEditor status={job.status} jobId={job.id} isEditable={isCareManager} />
              </div>
              <h2 id="job-detail-title" className="break-words text-2xl font-extrabold leading-tight tracking-tight text-[#342F43] sm:text-[30px]">{jobTitle}</h2>
              <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs font-medium text-[#636363] sm:text-sm">
                <span>Posted {formatRelativeDate(job.created_at)}</span>
                {job.created_by_name && <><span className="hidden h-1 w-1 rounded-full bg-[#A1AAD9] sm:inline-block" aria-hidden="true" /><span>Created by {job.created_by_name}</span></>}
              </div>
            </div>
          </div>
          <button
            onClick={() => setIsApplyModalOpen(true)}
            className="inline-flex min-h-[50px] w-full flex-shrink-0 items-center justify-center gap-2 rounded-2xl border border-[#FF885C] bg-[#FF885C] px-6 text-sm font-extrabold text-white shadow-[0_8px_20px_rgba(255,136,92,0.24)] transition hover:bg-[#F57A4F] focus:outline-none focus:ring-2 focus:ring-[#FF885C] focus:ring-offset-2 sm:w-auto"
          >
            <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" d="M12 5v14m7-7H5" /></svg>
            Apply Now
          </button>
        </div>
        <span className="pointer-events-none absolute -right-12 -top-20 h-48 w-48 rounded-full bg-[#FFE084]/35" aria-hidden="true" />
      </section>

      <div className="grid grid-cols-1 gap-x-5 gap-y-3 lg:grid-cols-12 lg:items-start">
        <div className="space-y-3 lg:col-span-8">
          <DetailSection 
            title="Care Information / Requirement"
            action={isCareManager && !job.order_id && (
              <button
                onClick={() => setIsEditPatientModalOpen(true)}
                className="inline-flex min-h-9 items-center gap-1.5 rounded-xl border border-[#A1AAD9]/45 bg-white px-3 text-xs font-bold text-[#5A46AA] shadow-sm hover:bg-[#A1AAD9]/10 focus:outline-none focus:ring-2 focus:ring-[#A1AAD9]"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                </svg>
                Edit
              </button>
            )}
          >
            <div className="space-y-4">
              <PatientDetailsWrapper job={job} />
              {job.remarks && (
                <div className="rounded-2xl border border-[#A1AAD9]/35 bg-[#FFFAED]/65 p-4 sm:p-5">
                  <div className="text-xs font-bold uppercase tracking-[0.04em] text-[#636363]">Remarks</div>
                  <p className="mt-2 whitespace-pre-wrap text-sm font-medium leading-6 text-[#342F43]">{job.remarks}</p>
                </div>
              )}
            </div>
          </DetailSection>
        </div>
        <aside className="space-y-3 lg:col-span-4 lg:sticky lg:top-24">
          <DetailSection title="Job Overview">
            <div className="grid grid-cols-1 gap-x-3 gap-y-2 sm:grid-cols-2 lg:grid-cols-1">
              <OverviewItem
                icon={<svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M20 7 12 3 4 7m16 0-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" /></svg>}
                label="Product/Service"
                value={productService}
              />
              {salary && (
                <OverviewItem
                  icon={<svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v.01M12 6v-1h4v1h-4z" /></svg>}
                  label="Estimated Job Salary"
                  value={salary}
                />
              )}
              <OverviewItem
                icon={<svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M21 13.255A23.931 23.931 0 0 1 12 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2" /></svg>}
                label="Experience"
                value={job.job_experience}
              />
              <OverviewItem
                icon={<svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 4.354a4 4 0 1 0 0 5.292M15 21H3v-1a6 6 0 0 1 12 0v1m0 0h6v-1a6 6 0 0 0-9-5.197" /></svg>}
                label="Gender Pref."
                value={job.job_gender}
              />
              <OverviewItem
                icon={<svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 1 1-8 0 4 4 0 0 1 8 0ZM5 21a7 7 0 0 1 14 0" /></svg>}
                label="Assignee"
                value={job.assigneeName}
              />
            </div>
          </DetailSection>
        </aside>
      </div>
      
      {isApplyModalOpen && (
        <ApplyJobModal
          jobId={job.id}
          jobTitle={jobTitle}
          onClose={() => setIsApplyModalOpen(false)}
          user={user}
        />
      )}

      {isEditPatientModalOpen && (
        <EditPatientDetailsModal
          job={job}
          onClose={() => setIsEditPatientModalOpen(false)}
          onSuccess={() => {
            setIsEditPatientModalOpen(false);
            window.location.reload();
          }}
        />
      )}
    </div>
  );
};

export default JobDetailPage;
